// pages/LoadTracking.js
import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Truck, Clock, Fuel, Activity, Package, Menu, ChevronLeft, ChevronRight, 
  Info, Navigation2, Radio, Smartphone, Settings, X, Check, AlertCircle,
  Satellite, RefreshCw, MapPin, Compass
} from 'lucide-react';
import { fetchUserLoads } from '../store/slices/loadSlice';
import { fetchCarrierLoads } from '../store/slices/loadCarrierSlice';
import SideNavigation from '../components/SideNavigation';
import useScreenSize from '../hooks/useScreenSize';
import { useNavigate } from 'react-router-dom';
import { logoutUser } from '../store/slices/authSlice';
import { fetchNotifications } from '../store/slices/notificationSlice';
import TrackingHistory from '../components/TrackingHistory';
import LoadDetails from '../components/LoadDetails';
import LoadTrackingMap from '../components/LoadTrackingMap';
import { formatTime, formatDateTime } from '../utils/formatters';
import api, { samsaraApi, loadTrackingApi } from '../utils/api';
import { toast } from 'react-toastify';
import { getLoadDisplayId } from '../utils/loadUtils';

// Format coordinates for display (e.g., "40.7128°N, 74.0060°W")
const formatCoordinates = (lat, lng) => {
  if (lat == null || lng == null) return null;
  const latDir = lat >= 0 ? 'N' : 'S';
  const lngDir = lng >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(4)}°${latDir}, ${Math.abs(lng).toFixed(4)}°${lngDir}`;
};

// Format coordinates in compact form (e.g., "40.7128, -74.0060")
const formatCoordinatesCompact = (lat, lng) => {
  if (lat == null || lng == null) return null;
  return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
};

// Tracking source configuration
const TRACKING_SOURCES = {
  driver_app: {
    label: 'LoadLink Driver App',
    icon: Smartphone,
    color: 'blue',
    description: 'Track via LoadLink mobile app'
  },
  samsara: {
    label: 'Samsara ELD',
    icon: Satellite,
    color: 'emerald',
    description: 'Track via Samsara telematics'
  },
  manual: {
    label: 'Manual Updates',
    icon: Radio,
    color: 'amber',
    description: 'Manual location updates only'
  },
  none: {
    label: 'No Tracking',
    icon: AlertCircle,
    color: 'gray',
    description: 'Tracking not configured'
  }
};

const LoadTracking = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobile, isTablet, isDesktop } = useScreenSize();
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop);
  const userType = useSelector(state => state.auth.userType);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const ITEMS_PER_PAGE = 10;
  const customerLoads = useSelector(state => state.load.loads);
  const carrierLoads = useSelector(state => state.loadCarrier.loads);
  const telematics = useSelector(state => state.telematics.activeTracking);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  
  // Breadcrumbs state
  const [selectedTimelineIndex, setSelectedTimelineIndex] = useState(-1);
  const [breadcrumbsEnabled, setBreadcrumbsEnabled] = useState(false);
  
  // Samsara integration state
  const [samsaraConnected, setSamsaraConnected] = useState(false);
  const [samsaraVehicles, setSamsaraVehicles] = useState([]);
  const [loadingSamsaraVehicles, setLoadingSamsaraVehicles] = useState(false);
  const [samsaraLocations, setSamsaraLocations] = useState({}); // Keyed by load_id
  const [showTrackingConfig, setShowTrackingConfig] = useState(false);
  const [updatingTrackingSource, setUpdatingTrackingSource] = useState(false);
  
  const isCarrier = userType === 'carrier' || userType === 'carrier_admin';
  
  const pagination = useSelector(state => isCarrier 
    ? state.loadCarrier.pagination 
    : state.load.pagination);

  useEffect(() => {
    setSidebarOpen(isDesktop);
  }, [isDesktop]);

  useEffect(() => {
    dispatch(fetchNotifications({ page: currentPage }));
  }, [dispatch, currentPage]);

  // Check Samsara connection status (carriers only)
  useEffect(() => {
    if (isCarrier) {
      checkSamsaraStatus();
    }
  }, [isCarrier]);

  // Fetch Samsara vehicles when connection is established
  useEffect(() => {
    if (samsaraConnected && isCarrier && samsaraVehicles.length === 0) {
      fetchSamsaraVehicles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [samsaraConnected, isCarrier]);

  const checkSamsaraStatus = async () => {
    try {
      const { data } = await samsaraApi.getStatus();
      setSamsaraConnected(data.connected);
    } catch (err) {
      setSamsaraConnected(false);
    }
  };

  // Fetch Samsara vehicles when connected
  const fetchSamsaraVehicles = useCallback(async () => {
    if (!samsaraConnected || !isCarrier) return;
    
    setLoadingSamsaraVehicles(true);
    try {
      const { data } = await samsaraApi.getVehicles();
      if (data.success) {
        setSamsaraVehicles(data.vehicles || []);
      }
    } catch (err) {
      console.error('Error fetching Samsara vehicles:', err);
    } finally {
      setLoadingSamsaraVehicles(false);
    }
  }, [samsaraConnected, isCarrier]);

  // Fetch Samsara location for loads using Samsara tracking
  const fetchSamsaraLocation = useCallback(async (loadId) => {
    try {
      const { data } = await samsaraApi.getLoadLocation(loadId);
      if (data.success && data.location) {
        setSamsaraLocations(prev => ({
          ...prev,
          [loadId]: data.location
        }));
      }
    } catch (err) {
      console.error(`Error fetching Samsara location for load ${loadId}:`, err);
    }
  }, []);

  // Fetch Samsara locations for selected load
  useEffect(() => {
    if (selectedLoad?.tracking_source === 'samsara' && selectedLoad?.samsara_vehicle_id) {
      fetchSamsaraLocation(selectedLoad.id);
      
      // Set up polling every 30 seconds
      const interval = setInterval(() => {
        fetchSamsaraLocation(selectedLoad.id);
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [selectedLoad, fetchSamsaraLocation]);

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/');
  };

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // Fetch loads on component mount
  useEffect(() => {
    if (isCarrier) {
      dispatch(fetchCarrierLoads({ 
        status: 'in_progress',
        page: currentPage,
        perPage: ITEMS_PER_PAGE,
        sortBy: 'pickup_date',
        sortOrder: 'asc'
      }));
    } else {
      dispatch(fetchUserLoads({ 
        status: 'in_progress',
        page: currentPage,
        perPage: ITEMS_PER_PAGE,
        sortBy: 'created_at',
        sortOrder: 'desc'
      }));
    }
  }, [dispatch, isCarrier, currentPage]);

  useEffect(() => {
    if (pagination) {
      setTotalPages(pagination.pages || 1);
    }
  }, [pagination]);

  // Reset breadcrumbs when load changes
  useEffect(() => {
    setSelectedTimelineIndex(-1);
    setBreadcrumbsEnabled(false);
  }, [selectedLoad?.id]);

  // Filter active loads
  const activeLoads = React.useMemo(() => {
    const loads = isCarrier ? carrierLoads : customerLoads;

    return loads.filter(load => {
      const status = load.status?.toLowerCase() || '';
      return (
        status === 'trip_started' ||
        status.startsWith('at_stop_') ||
        status.startsWith('in_transit_')
      ) && status !== 'completed' && status !== 'cancelled';
    });
  }, [isCarrier, carrierLoads, customerLoads]);

  const handleLoadSelect = (load) => {
    if (isMobile && selectedLoad?.id === load.id) {
      setSelectedLoad(null);
    } else {
      setSelectedLoad(load);
    }
  };

  const handleTimelineSelect = (index, trackingPoint) => {
    setSelectedTimelineIndex(index);
    if (!breadcrumbsEnabled) {
      setBreadcrumbsEnabled(true);
    }
  };

  const toggleBreadcrumbs = () => {
    setBreadcrumbsEnabled(!breadcrumbsEnabled);
    if (!breadcrumbsEnabled) {
      setSelectedTimelineIndex(-1);
    }
  };

  // Update tracking source for a load
  const handleUpdateTrackingSource = async (source, samsaraVehicle = null) => {
    if (!selectedLoad) return;
    
    setUpdatingTrackingSource(true);
    try {
      const payload = {
        tracking_source: source,
        samsara_vehicle_id: samsaraVehicle?.id || null,
        samsara_vehicle_name: samsaraVehicle?.name || null
      };
      
      const { data } = await loadTrackingApi.updateTrackingSource(selectedLoad.id, payload);
      
      if (data.success) {
        // Update the selected load with new tracking info
        setSelectedLoad(prev => ({
          ...prev,
          tracking_source: source,
          samsara_vehicle_id: samsaraVehicle?.id || null,
          samsara_vehicle_name: samsaraVehicle?.name || null
        }));
        
        toast.success(`Tracking source updated to ${TRACKING_SOURCES[source]?.label || source}`);
        setShowTrackingConfig(false);
        
        // If switched to Samsara, fetch location immediately
        if (source === 'samsara' && samsaraVehicle) {
          fetchSamsaraLocation(selectedLoad.id);
        }
      }
    } catch (err) {
      console.error('Error updating tracking source:', err);
      toast.error(err.response?.data?.error || 'Failed to update tracking source');
    } finally {
      setUpdatingTrackingSource(false);
    }
  };

  // Get current tracking data based on source
  const getCurrentTracking = (load) => {
    if (!load) return null;
    
    const source = load.tracking_source || 'driver_app';
    
    if (source === 'samsara') {
      const samsaraLoc = samsaraLocations[load.id];
      if (samsaraLoc) {
        return {
          location: {
            latitude: samsaraLoc.latitude,
            longitude: samsaraLoc.longitude
          },
          timestamp: samsaraLoc.timestamp,
          speed: samsaraLoc.speed,
          fuel_level: samsaraLoc.fuelPercent,
          vehicle: { 
            id: samsaraLoc.vehicleId, 
            name: samsaraLoc.vehicleName 
          },
          source: 'samsara',
          heading: samsaraLoc.heading,
          engineState: samsaraLoc.engineState
        };
      }
    }
    
    // Default to telematics from driver app
    return telematics[load.id];
  };

  // Tracking Source Badge Component
  const TrackingSourceBadge = ({ source, compact = false }) => {
    const config = TRACKING_SOURCES[source] || TRACKING_SOURCES.driver_app;
    const Icon = config.icon;
    
    const colorClasses = {
      blue: 'bg-blue-100 text-blue-700 border-blue-200',
      emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      amber: 'bg-amber-100 text-amber-700 border-amber-200',
      gray: 'bg-gray-100 text-gray-600 border-gray-200'
    };
    
    if (compact) {
      return (
        <div className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${colorClasses[config.color]}`}>
          <Icon className="w-3 h-3" />
        </div>
      );
    }
    
    return (
      <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border ${colorClasses[config.color]}`}>
        <Icon className="w-3.5 h-3.5" />
        <span>{config.label}</span>
      </div>
    );
  };

  // Load Card Component with tracking source indicator - Mobile optimized
  const LoadCard = ({ load }) => {
    const tracking = getCurrentTracking(load);
    const isSelected = selectedLoad?.id === load.id;
    const source = load.tracking_source || 'driver_app';
    
    const getAdjustedStatus = (status, sequence) => {
      const statusLower = status.toLowerCase();
      
      if (statusLower.startsWith('in_transit_')) {
        const parts = statusLower.split('_');
        if (parts.length === 4) {
          const currentStop = parseInt(parts[2], 10);
          const totalStops = parseInt(parts[3], 10);
          return isMobile ? `TRANSIT ${currentStop + 1}→${totalStops + 1}` : `IN TRANSIT ${currentStop + 1} → ${totalStops + 1}`;
        }
      }
      
      if (statusLower.startsWith('at_stop_')) {
        const parts = statusLower.split('_');
        const sequenceNum = parseInt(parts[parts.length - 1], 10);
        return `STOP ${sequenceNum + 1}`;
      }
      
      return status.replace(/_/g, ' ').toUpperCase();
    };
    
    return (
      <motion.div
        whileHover={{ scale: isMobile ? 1 : 1.01 }}
        className={`bg-white rounded-xl shadow-sm ${isMobile ? 'p-2' : 'p-3 sm:p-4'} mb-2 cursor-pointer border-2 transition-all duration-200 ${
          isSelected 
            ? 'border-blue-500 ring-2 ring-blue-100' 
            : 'border-transparent hover:border-gray-200'
        }`}
        onClick={() => handleLoadSelect(load)}
      >
        {/* Header - Compact on mobile */}
        <div className={`flex justify-between items-start ${isMobile ? 'gap-1' : 'gap-2 mb-2'}`}>
          <div className="flex-1 min-w-0">
            <div className={`flex items-center ${isMobile ? 'gap-1' : 'gap-2'} mb-0.5`}>
              <h3 className={`${isMobile ? 'text-xs' : 'text-base sm:text-lg'} font-semibold text-gray-900`}>
                {isMobile ? `#${getLoadDisplayId(load)}` : `Load ${getLoadDisplayId(load)}`}
              </h3>
              <TrackingSourceBadge source={source} compact />
            </div>
            <p className={`${isMobile ? 'text-[10px]' : 'text-xs sm:text-sm'} text-gray-600 truncate`}>
              {load.origin_city} → {load.destination_city}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <span className={`${isMobile ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-1 text-xs'} rounded-full font-medium whitespace-nowrap ${
              (load.status || '').toLowerCase().startsWith('in_transit') ? 'bg-green-100 text-green-800' : 
              (load.status || '').toLowerCase() === 'at_pickup' ? 'bg-yellow-100 text-yellow-800' :
              (load.status || '').toLowerCase() === 'at_delivery' ? 'bg-blue-100 text-blue-800' :
              (load.status || '').toLowerCase().startsWith('at_stop') ? 'bg-purple-100 text-purple-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {getAdjustedStatus(load.status, load.sequence)}
            </span>
            {!isMobile && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedLoad(load);
                  setIsDetailsOpen(true);
                }}
                className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="View Details"
              >
                <Info className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
        </div>
        
        {/* Tracking info - Only show on desktop or when expanded */}
        {tracking && !isMobile && (
          <div className="pt-2 border-t border-gray-100 space-y-3">
            {/* Coordinates Banner */}
            {tracking.location?.latitude != null && tracking.location?.longitude != null && (
              <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-slate-50 to-blue-50 rounded-lg border border-slate-200">
                <div className="p-1.5 bg-blue-500 rounded-md">
                  <Compass className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-500 font-medium">Current Position</p>
                  <p className="text-sm font-mono font-semibold text-slate-800 truncate">
                    {formatCoordinates(tracking.location.latitude, tracking.location.longitude)}
                  </p>
                </div>
              </div>
            )}
            
            {/* Tracking Stats Grid */}
            <div className="grid grid-cols-2 gap-2 sm:gap-4">
              <div className="flex items-center gap-1.5">
                <Truck className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-gray-500">Vehicle</p>
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {tracking.vehicle?.name || tracking.vehicle?.id || 'N/A'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-gray-500">Last Update</p>
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {tracking.timestamp ? formatTime(tracking.timestamp) : 'N/A'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-1.5">
                <Activity className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Speed</p>
                  <p className="text-sm font-medium text-gray-900">
                    {tracking.speed != null ? `${Math.round(tracking.speed)} mph` : 'N/A'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-1.5">
                <Fuel className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Fuel</p>
                  <p className="text-sm font-medium text-gray-900">
                    {tracking.fuel_level != null ? `${Math.round(tracking.fuel_level)}%` : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Mobile: Compact tracking indicator */}
        {tracking && isMobile && tracking.location?.latitude != null && (
          <div className="flex items-center gap-1.5 mt-1 pt-1 border-t border-gray-100">
            <Compass className="w-3 h-3 text-blue-500" />
            <span className="text-[10px] font-mono text-gray-600 truncate">
              {formatCoordinatesCompact(tracking.location.latitude, tracking.location.longitude)}
            </span>
            {tracking.speed != null && (
              <span className="text-[10px] text-gray-500 ml-auto">
                {Math.round(tracking.speed)} mph
              </span>
            )}
          </div>
        )}
        
        {/* Samsara-specific info */}
        {source === 'samsara' && load.samsara_vehicle_name && !isMobile && (
          <div className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-2">
            <Satellite className="w-3.5 h-3.5 text-emerald-600" />
            <span className="text-xs text-emerald-700 font-medium">
              {load.samsara_vehicle_name}
            </span>
          </div>
        )}
      </motion.div>
    );
  };

  // Tracking Configuration Panel
  const TrackingConfigPanel = () => {
    // Initialize state directly - no need for useEffect since modal recreates on each open
    const initialSource = selectedLoad?.tracking_source || 'driver_app';
    const initialSamsaraVehicle = selectedLoad?.samsara_vehicle_id 
      ? samsaraVehicles.find(v => v.id === selectedLoad.samsara_vehicle_id) || null
      : null;
    
    const [selectedSource, setSelectedSource] = useState(initialSource);
    const [selectedSamsaraVehicle, setSelectedSamsaraVehicle] = useState(initialSamsaraVehicle);
    
    // Platform vehicle/trailer state
    const [platformVehicles, setPlatformVehicles] = useState([]);
    const [platformTrailers, setPlatformTrailers] = useState([]);
    const [loadingPlatformVehicles, setLoadingPlatformVehicles] = useState(false);
    const [loadingPlatformTrailers, setLoadingPlatformTrailers] = useState(false);
    const [selectedPlatformVehicle, setSelectedPlatformVehicle] = useState(null);
    const [selectedPlatformTrailer, setSelectedPlatformTrailer] = useState(null);
    
    // Fetch platform vehicles when driver_app is selected
    useEffect(() => {
      if (selectedSource === 'driver_app' && platformVehicles.length === 0) {
        fetchPlatformVehicles();
      }
    }, [selectedSource]);
    
    // Fetch trailers when platform vehicle is selected
    useEffect(() => {
      if (selectedPlatformVehicle) {
        fetchPlatformTrailers(selectedPlatformVehicle.id);
      }
    }, [selectedPlatformVehicle]);
    
    const fetchPlatformVehicles = async () => {
      setLoadingPlatformVehicles(true);
      try {
        const response = await api.get('/api/carrier/available-vehicles');
        setPlatformVehicles(response.data || []);
      } catch (error) {
        console.error('Failed to fetch platform vehicles:', error);
        toast.error('Failed to load vehicles');
      } finally {
        setLoadingPlatformVehicles(false);
      }
    };
    
    const fetchPlatformTrailers = async (vehicleId) => {
      setLoadingPlatformTrailers(true);
      try {
        const response = await api.get(`/api/vehicles/${vehicleId}/available-trailers`);
        const available = response.data.available_trailers || [];
        const assigned = response.data.assigned_trailers || [];
        setPlatformTrailers([...available, ...assigned]);
      } catch (error) {
        console.error('Failed to fetch platform trailers:', error);
      } finally {
        setLoadingPlatformTrailers(false);
      }
    };
    
    const handleSave = async () => {
      if (selectedSource === 'samsara' && !selectedSamsaraVehicle) {
        toast.warning('Please select a Samsara vehicle');
        return;
      }
      
      if (selectedSource === 'driver_app' && !selectedPlatformVehicle) {
        toast.warning('Please select a platform vehicle');
        return;
      }
      
      // For Samsara, just update tracking source
      if (selectedSource === 'samsara') {
        handleUpdateTrackingSource(selectedSource, selectedSamsaraVehicle);
        return;
      }
      
      // For platform tracking, we need to assign vehicle (and optionally trailer)
      try {
        setUpdatingTrackingSource(true);
        
        // Step 1: Update tracking source to driver_app
        await loadTrackingApi.updateTrackingSource(selectedLoad.id, {
          tracking_source: 'driver_app',
          samsara_vehicle_id: null,
          samsara_vehicle_name: null
        });
        
        // Step 2: Assign platform vehicle
        await api.post(`/api/loads/${selectedLoad.id}/assign-vehicle`, {
          vehicle_id: selectedPlatformVehicle.id
        });
        
        // Step 3: Assign trailer if selected
        if (selectedPlatformTrailer) {
          await api.post(`/api/loads/${selectedLoad.id}/assign-trailer`, {
            trailer_id: selectedPlatformTrailer.id
          });
        }
        
        // Update local state
        setSelectedLoad(prev => ({
          ...prev,
          tracking_source: 'driver_app',
          samsara_vehicle_id: null,
          samsara_vehicle_name: null,
          assigned_vehicle_id: selectedPlatformVehicle.id,
          assigned_trailer_id: selectedPlatformTrailer?.id || null
        }));
        
        toast.success('Tracking configured with LoadLink Driver App');
        setShowTrackingConfig(false);
      } catch (error) {
        console.error('Error configuring platform tracking:', error);
        toast.error(error.response?.data?.error || 'Failed to configure tracking');
      } finally {
        setUpdatingTrackingSource(false);
      }
    };
    
    return (
      <AnimatePresence>
        {showTrackingConfig && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowTrackingConfig(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Configure Tracking</h3>
                  <p className="text-sm text-gray-500">Load {getLoadDisplayId(selectedLoad)}</p>
                </div>
                <button
                  onClick={() => setShowTrackingConfig(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              {/* Content */}
              <div className="p-6 space-y-6 overflow-y-auto max-h-[60vh]">
                {/* Tracking Source Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Tracking Source
                  </label>
                  <div className="space-y-2">
                    {Object.entries(TRACKING_SOURCES).filter(([key]) => key !== 'none').map(([key, config]) => {
                      const Icon = config.icon;
                      const isActive = selectedSource === key;
                      const isDisabled = key === 'samsara' && !samsaraConnected;
                      
                      return (
                        <button
                          key={key}
                          onClick={() => !isDisabled && setSelectedSource(key)}
                          disabled={isDisabled}
                          className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                            isActive 
                              ? 'border-blue-500 bg-blue-50' 
                              : isDisabled
                                ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className={`p-2 rounded-lg ${
                            isActive ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'
                          }`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 text-left">
                            <p className={`font-medium ${isActive ? 'text-blue-900' : 'text-gray-900'}`}>
                              {config.label}
                            </p>
                            <p className="text-sm text-gray-500">
                              {isDisabled ? 'Samsara not connected' : config.description}
                            </p>
                          </div>
                          {isActive && (
                            <Check className="w-5 h-5 text-blue-500" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Samsara Vehicle Selection */}
                {selectedSource === 'samsara' && samsaraConnected && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Select Samsara Vehicle
                    </label>
                    {loadingSamsaraVehicles ? (
                      <div className="flex items-center justify-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
                        <span className="ml-2 text-gray-600">Loading vehicles...</span>
                      </div>
                    ) : samsaraVehicles.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Truck className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No vehicles found in Samsara</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {samsaraVehicles.map((vehicle) => (
                          <button
                            key={vehicle.id}
                            onClick={() => setSelectedSamsaraVehicle(vehicle)}
                            className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                              selectedSamsaraVehicle?.id === vehicle.id
                                ? 'border-emerald-500 bg-emerald-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <Truck className={`w-5 h-5 ${
                              selectedSamsaraVehicle?.id === vehicle.id ? 'text-emerald-600' : 'text-gray-400'
                            }`} />
                            <div className="flex-1 text-left">
                              <p className="font-medium text-gray-900">{vehicle.name}</p>
                              {vehicle.licensePlate && (
                                <p className="text-xs text-gray-500">{vehicle.licensePlate}</p>
                              )}
                            </div>
                            {selectedSamsaraVehicle?.id === vehicle.id && (
                              <Check className="w-5 h-5 text-emerald-500" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Platform Vehicle Selection */}
                {selectedSource === 'driver_app' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Select Platform Vehicle
                    </label>
                    {loadingPlatformVehicles ? (
                      <div className="flex items-center justify-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
                        <span className="ml-2 text-gray-600">Loading vehicles...</span>
                      </div>
                    ) : platformVehicles.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Truck className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No vehicles found</p>
                        <p className="text-xs mt-1">Add vehicles in Settings</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {platformVehicles.map((vehicle) => (
                          <button
                            key={vehicle.id}
                            onClick={() => setSelectedPlatformVehicle(vehicle)}
                            className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                              selectedPlatformVehicle?.id === vehicle.id
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <Truck className={`w-5 h-5 ${
                              selectedPlatformVehicle?.id === vehicle.id ? 'text-blue-600' : 'text-gray-400'
                            }`} />
                            <div className="flex-1 text-left">
                              <p className="font-medium text-gray-900">
                                {vehicle.year} {vehicle.make} {vehicle.model}
                              </p>
                              <p className="text-xs text-gray-500">
                                {vehicle.type} • {vehicle.license_plate || 'No plate'}
                              </p>
                            </div>
                            {selectedPlatformVehicle?.id === vehicle.id && (
                              <Check className="w-5 h-5 text-blue-500" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Platform Trailer Selection */}
                {selectedSource === 'driver_app' && selectedPlatformVehicle && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Select Trailer <span className="text-gray-400">(Optional)</span>
                    </label>
                    {loadingPlatformTrailers ? (
                      <div className="flex items-center justify-center py-4">
                        <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
                        <span className="ml-2 text-gray-600 text-sm">Loading trailers...</span>
                      </div>
                    ) : platformTrailers.length === 0 ? (
                      <div className="text-center py-4 text-gray-500 text-sm">
                        <Package className="w-8 h-8 mx-auto mb-1 text-gray-300" />
                        <p>No trailers available for this vehicle</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {platformTrailers.map((trailer) => (
                          <button
                            key={trailer.id}
                            onClick={() => setSelectedPlatformTrailer(
                              selectedPlatformTrailer?.id === trailer.id ? null : trailer
                            )}
                            className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                              selectedPlatformTrailer?.id === trailer.id
                                ? 'border-amber-500 bg-amber-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <Package className={`w-5 h-5 ${
                              selectedPlatformTrailer?.id === trailer.id ? 'text-amber-600' : 'text-gray-400'
                            }`} />
                            <div className="flex-1 text-left">
                              <p className="font-medium text-gray-900">
                                {trailer.trailer_number || `Trailer #${trailer.id}`}
                              </p>
                              <p className="text-xs text-gray-500">
                                {trailer.trailer_type} • {trailer.length}ft
                              </p>
                            </div>
                            {selectedPlatformTrailer?.id === trailer.id && (
                              <Check className="w-5 h-5 text-amber-500" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowTrackingConfig(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={
                    updatingTrackingSource || 
                    (selectedSource === 'samsara' && !selectedSamsaraVehicle) ||
                    (selectedSource === 'driver_app' && !selectedPlatformVehicle)
                  }
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {updatingTrackingSource && <RefreshCw className="w-4 h-4 animate-spin" />}
                  Save Changes
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  const PaginationControls = () => {
    const handlePreviousPage = () => {
      if (currentPage > 1) {
        setCurrentPage(prev => prev - 1);
      }
    };

    const handleNextPage = () => {
      if (currentPage < totalPages) {
        setCurrentPage(prev => prev + 1);
      }
    };

    if (activeLoads.length === 0) return null;

    return (
      <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 rounded-b-lg relative z-10">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            className={`relative inline-flex items-center rounded-md px-4 py-2 text-sm font-medium z-20 ${
              currentPage === 1
                ? 'bg-gray-100 text-gray-400'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Previous
          </button>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className={`relative ml-3 inline-flex items-center rounded-md px-4 py-2 text-sm font-medium z-20 ${
              currentPage === totalPages
                ? 'bg-gray-100 text-gray-400'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing page <span className="font-medium">{currentPage}</span> of{' '}
              <span className="font-medium">{totalPages}</span>
            </p>
          </div>
          <div className="relative z-20">
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                type="button"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 ${
                  currentPage === 1 ? 'cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                <span className="sr-only">Previous</span>
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 ${
                  currentPage === totalPages ? 'cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                <span className="sr-only">Next</span>
                <ChevronRight className="h-5 w-5" aria-hidden="true" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  // Get tracking data for the selected load
  const selectedLoadTracking = getCurrentTracking(selectedLoad);

  return (
    <div className="bg-gray-50 min-h-screen flex">
      <SideNavigation 
        isOpen={sidebarOpen} 
        onToggle={toggleSidebar} 
        onLogout={handleLogout}
        className="z-50"
      />

      <main className={`flex-1 overflow-x-hidden overflow-y-auto transition-all duration-300 relative ${
        isMobile || isTablet ? 'ml-0' : (sidebarOpen ? 'ml-64' : 'ml-16')
      }`}>
        <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-4">
          {/* Enhanced Header - Mobile optimized */}
          <div className={`flex items-center justify-between ${isMobile ? 'mb-2' : 'mb-4'}`}>
            <div className="flex items-center gap-2 sm:gap-4">
              {(isMobile || isTablet) && (
                <button
                  onClick={toggleSidebar}
                  className={`${isMobile ? 'p-1.5' : 'p-2'} bg-white rounded-lg shadow-sm hover:bg-gray-50 transition-colors`}
                >
                  <Menu className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-gray-700`} />
                </button>
              )}
              <div>
                <h1 className={`${isMobile ? 'text-base' : 'text-xl'} font-bold text-gray-900`}>Load Tracking</h1>
                {samsaraConnected && isCarrier && !isMobile && (
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Satellite className="w-3.5 h-3.5 text-emerald-500" />
                    <span className="text-xs text-emerald-600 font-medium">Samsara Connected</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Controls - Compact on mobile */}
            <div className="flex items-center gap-1.5 sm:gap-2">
              {/* Tracking Config Button (Carriers only) */}
              {isCarrier && selectedLoad && (
                <button
                  onClick={() => setShowTrackingConfig(true)}
                  className={`flex items-center gap-1.5 ${isMobile ? 'px-2 py-1.5' : 'px-3 py-2'} bg-white text-gray-700 rounded-lg hover:bg-gray-50 border border-gray-200 ${isMobile ? 'text-xs' : 'text-sm'} font-medium transition-colors shadow-sm`}
                >
                  <Settings className={`${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />
                  {!isMobile && 'Tracking'}
                </button>
              )}
              
              {/* Journey Mode Toggle */}
              {selectedLoad && (
                <button
                  onClick={toggleBreadcrumbs}
                  className={`flex items-center gap-1.5 ${isMobile ? 'px-2 py-1.5' : 'px-3 py-2'} rounded-lg ${isMobile ? 'text-xs' : 'text-sm'} font-medium transition-all duration-200 shadow-sm ${
                    breadcrumbsEnabled
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  <Navigation2 className={`${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />
                  {!isMobile && 'Journey Mode'}
                </button>
              )}
            </div>
          </div>

          {/* Selected Load Info Banner - Compact on mobile */}
          {selectedLoad && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`${isMobile ? 'mb-2 p-2' : 'mb-4 p-3'} bg-white rounded-xl shadow-sm border border-gray-200`}
            >
              <div className={`flex ${isMobile ? 'flex-col gap-1.5' : 'items-center justify-between flex-wrap gap-2'}`}>
                <div className="flex items-center gap-2">
                  <div className={`${isMobile ? 'p-1.5' : 'p-2'} bg-blue-100 rounded-lg`}>
                    <Truck className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-blue-600`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`${isMobile ? 'text-sm' : 'text-base'} font-semibold text-gray-900`}>
                        {isMobile ? `#${getLoadDisplayId(selectedLoad)}` : `Load ${getLoadDisplayId(selectedLoad)}`}
                      </p>
                      {isMobile && <TrackingSourceBadge source={selectedLoad.tracking_source || 'driver_app'} compact />}
                    </div>
                    <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-500 truncate`}>
                      {selectedLoad.origin_city} → {selectedLoad.destination_city}
                    </p>
                  </div>
                </div>
                
                {/* Mobile: Coordinates in a row; Desktop: All badges */}
                {isMobile ? (
                  selectedLoadTracking?.location?.latitude != null && selectedLoadTracking?.location?.longitude != null && (
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-50 rounded-lg border border-slate-200 ml-8">
                      <MapPin className="w-3 h-3 text-slate-500" />
                      <span className="text-[10px] font-mono font-medium text-slate-700">
                        {formatCoordinatesCompact(selectedLoadTracking.location.latitude, selectedLoadTracking.location.longitude)}
                      </span>
                    </div>
                  )
                ) : (
                  <div className="flex items-center gap-3 flex-wrap">
                    {selectedLoadTracking?.location?.latitude != null && selectedLoadTracking?.location?.longitude != null && (
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 rounded-lg border border-slate-200">
                        <MapPin className="w-3.5 h-3.5 text-slate-600" />
                        <span className="text-xs font-mono font-medium text-slate-700">
                          {formatCoordinatesCompact(selectedLoadTracking.location.latitude, selectedLoadTracking.location.longitude)}
                        </span>
                      </div>
                    )}
                    <TrackingSourceBadge source={selectedLoad.tracking_source || 'driver_app'} />
                    {selectedLoad.samsara_vehicle_name && (
                      <span className="text-sm text-gray-600">
                        {selectedLoad.samsara_vehicle_name}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Main Content - Improved mobile layout */}
          <div className={`flex flex-col ${isMobile ? '' : 'md:flex-row'}`} style={{ height: isMobile ? 'calc(100vh - 8rem)' : 'calc(100vh - 10rem)' }}>
            {/* Load List - Collapsible on mobile when load is selected */}
            <div className={`
              ${isMobile 
                ? (selectedLoad ? 'h-auto max-h-32 overflow-hidden' : 'h-full') 
                : 'md:w-1/3 h-full'
              } 
              bg-white rounded-xl shadow-sm ${isMobile ? 'mb-2' : 'md:mb-0 md:mr-4'} overflow-hidden transition-all duration-300 border border-gray-200
            `}>
              <div className={`${isMobile && selectedLoad ? 'h-32' : 'h-full'} overflow-y-auto`}>
                <div className="p-2 sm:p-4">
                  <div className="flex items-center justify-between mb-2 sm:mb-4">
                    <h2 className={`${isMobile ? 'text-sm' : 'text-lg'} font-bold text-gray-900`}>Active Loads</h2>
                    <span className={`${isMobile ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs'} bg-blue-100 text-blue-700 font-medium rounded-full`}>
                      {activeLoads.length}
                    </span>
                  </div>
                  {activeLoads.length > 0 ? (
                    <>
                      <div className="space-y-2">
                        {activeLoads.map(load => (
                          <LoadCard key={load.id} load={load} />
                        ))}
                      </div>
                      {!isMobile && <PaginationControls />}
                    </>
                  ) : (
                    <div className={`text-center ${isMobile ? 'py-6' : 'py-12'}`}>
                      <Package className={`${isMobile ? 'w-8 h-8' : 'w-12 h-12'} mx-auto mb-2 text-gray-300`} />
                      <p className={`${isMobile ? 'text-xs' : 'text-base'} text-gray-500 font-medium`}>No active loads</p>
                      {!isMobile && <p className="text-sm text-gray-400 mt-1">Active loads will appear here</p>}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Map and Tracking History - Takes more space on mobile */}
            <div className={`flex-1 flex flex-col ${isMobile ? 'min-h-0' : ''}`}>
              {/* Map Container - Larger on mobile when load is selected */}
              <div className={`${isMobile ? (selectedLoad ? 'flex-1 min-h-[200px]' : 'h-48') : 'flex-1'} mb-2 relative bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200`}>
                {selectedLoad ? (
                  <div className="h-full w-full">
                    <LoadTrackingMap
                      load={selectedLoad}
                      telematics={selectedLoadTracking}
                      selectedTimelineIndex={selectedTimelineIndex}
                      showBreadcrumbs={breadcrumbsEnabled}
                    />
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                    <div className="text-center p-4">
                      <div className={`${isMobile ? 'w-12 h-12' : 'w-20 h-20'} mx-auto mb-3 bg-white rounded-xl shadow-sm flex items-center justify-center`}>
                        <Package className={`${isMobile ? 'w-6 h-6' : 'w-10 h-10'} text-gray-400`} />
                      </div>
                      <p className={`${isMobile ? 'text-sm' : 'text-base'} text-gray-600 font-medium`}>Select a load to view tracking</p>
                      {!isMobile && <p className="text-sm text-gray-400 mt-1">Choose from the list on the left</p>}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Tracking History - Compact on mobile */}
              {selectedLoad && (
                <div className={`${isMobile ? 'h-36 min-h-[144px]' : 'h-2/5 min-h-[280px]'} overflow-hidden bg-white rounded-xl shadow-sm border border-gray-200`}>
                  <TrackingHistory 
                    loadId={selectedLoad.id}
                    onTimelineSelect={handleTimelineSelect}
                    selectedTimelineIndex={selectedTimelineIndex}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Tracking Configuration Modal */}
      <TrackingConfigPanel />

      {selectedLoad && (
        <LoadDetails
          load={selectedLoad}
          isOpen={isDetailsOpen}
          onClose={() => setIsDetailsOpen(false)}
          showFullAddress={true}
          className="z-50"
        />
      )}
    </div>
  );
};

export default LoadTracking;
